import { z } from 'zod'

import { bsDateSchema, moneySchema } from '@/schemas/common'
import { tablePageSchema, type PageSize } from '@/schemas/table'

/**
 * Public issues: the list on Public Issues and the detail page an issue opens.
 *
 * Shaped after `GET /api/v5/public-issues/`, which normalises three legacy
 * tables (IPO, right share, auction) into one card. Anything a type does not
 * carry is null rather than zero: an auction has no price until its cut-off is
 * set, and a zero would read as a free share.
 */

/** mm-be's type rail. `migrant` and `qii` are IPO categories with their own tab. */
export const issueTypeSchema = z.enum([
  'ipo',
  'fpo',
  'migrant',
  'qii',
  'mutual_fund',
  'bonds',
  'right',
  'auction',
])

/** Derived by mm-be from the dates against today in Nepal time, never stored. */
export const issueStatusSchema = z.enum(['open', 'upcoming', 'closed'])

/**
 * What the Public Issues PAGE can be asked to show: the types and windows mm-be serves plus the
 * mock's two it does not yet (`local` = IPO-Local, `pipeline`; `asks/pi-public-issues.md` A1, A2).
 * Kept apart from the wire's own enums so an unserved value never reaches a request: the screen
 * draws "Not available yet" for it (`ipoAvailability.ts`).
 */
export const issueListTypeSchema = z.union([issueTypeSchema, z.literal('local')])
export const issueListStatusSchema = z.union([issueStatusSchema, z.literal('pipeline')])

export const issueRowSchema = z.object({
  /** Composite id — "ipo-123", "right-45", "auction-9" — and the detail route's param. */
  key: z.string().min(1),
  type: issueTypeSchema,
  typeLabel: z.string().min(1),
  symbol: z.string().min(1).nullable(),
  company: z.string().min(1),
  /** The "Migrant" / "QII" badge. IPOs only. */
  category: z.string().min(1).nullable(),
  /** The same sector `IssueDetail` carries. Drives the Closed tab's own filter. */
  sector: z.string().min(1).nullable(),
  price: moneySchema.nullable(),
  /** What `price` means for this type: "Issue Price", "Right Price", "Cut-off Price". */
  priceLabel: z.string().min(1),
  /** "At par" / "At premium" — the price's own second line. Legacy rows often lack it. */
  pricingBadge: z.string().min(1).nullable(),
  /** Kitta or units on offer — a count, so a number. */
  units: z.number().int().nonnegative().nullable(),
  /** "1:4" — Right issues only; every other type leaves it null. */
  ratio: z.string().min(1).nullable(),
  /** "ordinary" / "promoter" — Auctions only; every other type leaves it null. */
  shareType: z.string().min(1).nullable(),
  opens: bsDateSchema.nullable(),
  closes: bsDateSchema.nullable(),
  status: issueStatusSchema,
  /** A real link only — legacy sometimes stores a bare file name instead. */
  prospectus: z.url().nullable(),
})

export const issuesPageSchema = tablePageSchema(issueRowSchema)

/** One tile in the four-up strip: "Issue size / 30,00,000 kitta / Rs 30.00 Cr". */
export const ipoFactSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  hint: z.string().min(1),
})

/** One dot on the dates timeline. States match `StepTimeline`. */
export const ipoStepSchema = z.object({
  label: z.string().min(1),
  sublabel: z.string().min(1),
  state: z.enum(['done', 'current', 'upcoming']),
})

/** One row of "The issue, in detail". Tones match `KeyValueList`. */
export const ipoDetailRowSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  tone: z.enum(['neutral', 'up', 'accent']),
})

/**
 * The Demand card's figures (the mock's `pi-demand`): subscription as a multiple of the issue, the
 * day of the window, and the odds as "1 in N". mm-be serves none of it yet (`asks/pi-public-issues.md`
 * A5), so the service never fills it; the card stays behind `DEMAND_AVAILABLE`.
 */
export const ipoDemandSchema = z.object({
  multiple: z.number().positive(),
  day: z.number().int().positive().nullable(),
  days: z.number().int().positive().nullable(),
  oddsOneIn: z.number().positive().nullable(),
})

export const issueDetailSchema = z.object({
  key: z.string().min(1),
  type: issueTypeSchema,
  typeLabel: z.string().min(1),
  status: issueStatusSchema,
  company: z.string().min(1),
  symbol: z.string().min(1).nullable(),
  sector: z.string().min(1).nullable(),
  priceLine: z.string().min(1),
  /** The primary button's words for the window the issue is in: "Apply on MeroShare", "Opens Bhadra 4". */
  applyLabel: z.string().min(1),
  /** "Bhadra 4": the short BS dates the how-to-apply card names. Null when the issue has none. */
  opens: z.string().min(1).nullable(),
  closes: z.string().min(1).nullable(),
  facts: z.array(ipoFactSchema).length(4),
  /**
   * Only the dates the issue actually has, in date order. Legacy rows are
   * often half-filled, so this can be empty, and the screen drops the card
   * rather than drawing a timeline of blanks.
   */
  timeline: z.array(ipoStepSchema),
  details: z.array(ipoDetailRowSchema),
  prospectus: z.url().nullable(),
  demand: ipoDemandSchema.nullable().optional(),
})

/** One same-sector company on the peers card: its issue price against today's. */
export const issuePeerSchema = z.object({
  symbol: z.string().min(1),
  issuePrice: moneySchema.nullable(),
  lastPrice: moneySchema,
  /** Null when the issue price was never recorded — never a made-up 0%. */
  gainPercent: moneySchema.nullable(),
})

export const ipoListPageSchema = z.object({
  rows: z.array(issueRowSchema),
  nextCursor: z.string().optional(),
  total: z.number().int().nonnegative(),
})

/** What the Public Issues table asks for. No `type` means every type. */
export interface IssueQuery {
  type?: IssueType | undefined
  status: IssueStatus
  page: number
  pageSize: PageSize
}

export type IssueType = z.infer<typeof issueTypeSchema>
export type IssueStatus = z.infer<typeof issueStatusSchema>
export type IssueListType = z.infer<typeof issueListTypeSchema>
export type IssueListStatus = z.infer<typeof issueListStatusSchema>
export type IssueRow = z.infer<typeof issueRowSchema>
export type IssuesPage = z.infer<typeof issuesPageSchema>
export type IpoFact = z.infer<typeof ipoFactSchema>
export type IpoStep = z.infer<typeof ipoStepSchema>
export type IpoDetailRow = z.infer<typeof ipoDetailRowSchema>
export type IpoDemand = z.infer<typeof ipoDemandSchema>
export type IssueDetail = z.infer<typeof issueDetailSchema>
export type IssuePeer = z.infer<typeof issuePeerSchema>
export type IpoListPage = z.infer<typeof ipoListPageSchema>
